/**
 * Per-player tactical brief engine.
 *
 * Given a saved `TacticsLayout` + a side + a player key, produces a
 * 4-section brief ("in possession" / "out of possession" / "transitions"
 * / "set pieces") that the coach can hand to that player. The engine is
 * pure, deterministic and rule-based — no AI. Phase 5 layers a Claude
 * polish pass on top; the rule output stays the spec §14.2 fallback.
 *
 * Slot taxonomy comes from `./positionRoles` (`GK`/`LB`/`CB`/`RB`/`LWB`/
 * `RWB`/`CDM`/`CM`/`LM`/`RM`/`CAM`/`LW`/`RW`/`ST`). Named-role
 * vocabulary comes from `./roles` (`false-9` / `ball-playing` /
 * `mezzala` etc.). Team-wide context comes from `./teamTactics`
 * (`buildUpStyle` / `defensiveApproach` / 6 sliders).
 *
 * Bullets per section are deduped, capped at 3, in declaration order.
 */

import type { TacticsArrow, TacticsLayout } from './types';
import type { BuildUpStyle, DefensiveApproach, TeamTacticsConfig } from './teamTactics';
import { defaultTacticsConfig, isTeamTacticsBundle } from './teamTactics';
import type { PlayerRoleSelection } from './roles';
import { isPlayerRoleSelection } from './roles';
import type { TacticsSetPiece } from './setPieces';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type BriefSource = 'rules' | 'ai' | 'manual';

export interface PlayerBrief {
  playerKey: string;
  slot: string;
  namedRole: string | null;
  inPossession: string[];
  outOfPossession: string[];
  transitions: string[];
  setPieces: string[];
  source: BriefSource;
  generatedAt: string;
}

export interface PlayerBriefInput {
  layout: TacticsLayout;
  side: 'home' | 'away';
  playerKey: string;
}

// ---------------------------------------------------------------------------
// Slot baseline responsibilities
// ---------------------------------------------------------------------------

interface SlotResp {
  inPoss: string[];
  outOfPoss: string[];
  transitions: string[];
}

const SLOT_RESPONSIBILITIES: Record<string, SlotResp> = {
  GK: {
    inPoss: ['Start build-up — feet on the ball when their press triggers'],
    outOfPoss: [
      'Sweep behind the back line on through-balls',
      'Communicate the line height to your back four',
    ],
    transitions: ['Quick release to the full-backs when we win it back'],
  },
  LB: {
    inPoss: ['Overlap or underlap depending on the LW’s movement'],
    outOfPoss: ['Stay tight to their RW — don’t let them turn'],
    transitions: ['First pass forward to the LW when we win the ball'],
  },
  RB: {
    inPoss: ['Overlap or underlap depending on the RW’s movement'],
    outOfPoss: ['Stay tight to their LW — don’t let them turn'],
    transitions: ['First pass forward to the RW when we win the ball'],
  },
  LWB: {
    inPoss: ['Push high — give the LCM an outlet on the touchline'],
    outOfPoss: ['Drop into the back five when we lose the ball'],
    transitions: ['Sprint the channel to stretch them as we break'],
  },
  RWB: {
    inPoss: ['Push high — give the RCM an outlet on the touchline'],
    outOfPoss: ['Drop into the back five when we lose the ball'],
    transitions: ['Sprint the channel to stretch them as we break'],
  },
  CB: {
    inPoss: [
      'Split to the edges of the box when we build',
      'Step into midfield if they leave you free',
    ],
    outOfPoss: ['Hold the line — no one runs in behind you'],
    transitions: ['Squeeze up immediately when we win it back'],
  },
  CDM: {
    inPoss: ['Drop between the CBs to receive on the half-turn'],
    outOfPoss: ['Screen passes into their #10'],
    transitions: ['Be the first option for the recovery pass'],
  },
  CM: {
    inPoss: [
      'Rotate with the CDM — one drops, one stays high',
      'Find space between their lines',
    ],
    outOfPoss: ['Cover the half-space when their winger comes inside'],
    transitions: ['Drive forward with the ball if we win it in midfield'],
  },
  LM: {
    inPoss: ['Hug the touchline when we have it; come inside on the cycle'],
    outOfPoss: ['Track their RB on their overlaps'],
    transitions: ['Show wide for the switch when we recover'],
  },
  RM: {
    inPoss: ['Hug the touchline when we have it; come inside on the cycle'],
    outOfPoss: ['Track their LB on their overlaps'],
    transitions: ['Show wide for the switch when we recover'],
  },
  CAM: {
    inPoss: [
      'Find pockets between their CBs and CDM',
      'Spin in behind when the ST drops',
    ],
    outOfPoss: ['Cover-shadow their CDM so we cut the line through midfield'],
    transitions: ['Be the second option breaking through the middle'],
  },
  LW: {
    inPoss: [
      'Stay high and wide until the ball is on your side',
      'Attack the back post on switches',
    ],
    outOfPoss: ['First defender from the front — force their RB inside'],
    transitions: ['Run the channel between their RB and RCB'],
  },
  RW: {
    inPoss: [
      'Stay high and wide until the ball is on your side',
      'Attack the back post on switches',
    ],
    outOfPoss: ['First defender from the front — force their LB inside'],
    transitions: ['Run the channel between their LB and LCB'],
  },
  ST: {
    inPoss: [
      'Pin the deepest CB — don’t let them step',
      'Make diagonal runs across the back line',
    ],
    outOfPoss: ['Cut the passing lane into their CDM'],
    transitions: ['Stretch behind the last man immediately'],
  },
};

// ---------------------------------------------------------------------------
// Build-up + defensive-approach modifiers
// ---------------------------------------------------------------------------

type SlotModifierMap<K extends string> = Partial<Record<string, Partial<Record<K, Partial<SlotResp>>>>>;

const BUILD_UP_MODIFIERS: SlotModifierMap<BuildUpStyle> = {
  CM: {
    'short-pass': { inPoss: ['Drop into the LCB-LB channel to receive between the lines'] },
    'long-ball': { inPoss: ['Win the second balls after the GK clearance'] },
  },
  CDM: {
    'short-pass': { inPoss: ['Drop between the CBs to play out from the back'] },
  },
  ST: {
    'long-ball': { inPoss: ['Win the first contact off the GK clearance'] },
    'direct': { inPoss: ['Run the channel for the long diagonal'] },
  },
  GK: {
    'short-pass': { inPoss: ['Play through the press — no panic clearances'] },
    'long-ball': { inPoss: ['Hit the long ball when their press is set'] },
  },
  CB: {
    'short-pass': { inPoss: ['Carry the ball forward if they don’t press'] },
    'long-ball': { inPoss: ['Look for the diagonal into the channel'] },
  },
};

const DEFENSIVE_MODIFIERS: SlotModifierMap<DefensiveApproach> = {
  ST: {
    'high-press': { outOfPoss: ['Press the deepest CB on goal-kicks; force them to play long'] },
    'counter-press': { outOfPoss: ['First defender on the ball-carrier the moment we lose it'] },
    'low-block': { outOfPoss: ['Drop onto their CDM when we set our block'] },
  },
  LW: {
    'high-press': { outOfPoss: ['Curve your press from the outside in'] },
    'low-block': { outOfPoss: ['Drop into a left-mid in our block of four'] },
  },
  RW: {
    'high-press': { outOfPoss: ['Curve your press from the outside in'] },
    'low-block': { outOfPoss: ['Drop into a right-mid in our block of four'] },
  },
  CDM: {
    'low-block': { outOfPoss: ['Screen the half-space — don’t step out of the block'] },
    'counter-press': { outOfPoss: ['Hunt the second ball after our forwards trigger'] },
  },
  CB: {
    'high-press': { outOfPoss: ['Push the line up to halfway when we press'] },
    'low-block': { outOfPoss: ['Drop into a back five and protect the box'] },
  },
};

// ---------------------------------------------------------------------------
// Named-role overrides — replace the slot baseline for that section
// ---------------------------------------------------------------------------

const ROLE_OVERRIDES: Record<string, Partial<SlotResp>> = {
  'false-9': {
    inPoss: [
      'Drop into the 10 space to drag a CB out',
      'Combine with the wingers — one-twos round the corner',
    ],
  },
  target: {
    inPoss: [
      'Hold the ball up — wait for runners off you',
      'Win the first ball; the CM picks up the second',
    ],
  },
  poacher: {
    inPoss: [
      'Stay on the last shoulder — let the AM build the play',
      'Attack any cross that hits the six-yard area',
    ],
  },
  'pressing-forward': {
    inPoss: ['Stretch behind the last man'],
    outOfPoss: ['Trigger the press on backwards passes'],
  },
  'sweeper-keeper': {
    inPoss: ['Step out to the edge of the box when we push up'],
    outOfPoss: ['Aggressive sweeping on any through-ball'],
  },
  'ball-playing': {
    inPoss: ['Step into midfield and break a line with the pass'],
  },
  stopper: {
    outOfPoss: ['Aggressively step on the striker when they drop'],
  },
  'wing-back': {
    inPoss: ['Push high — give the side a five-man attack'],
  },
  'inverted-full-back': {
    inPoss: ['Tuck into midfield in possession — leave the wing to the winger'],
  },
  mezzala: {
    inPoss: ['Drift into the half-space to combine with the winger'],
  },
  'deep-lying-playmaker': {
    inPoss: ['Drop deep, dictate tempo, switch the play to the far side'],
  },
  'box-to-box': {
    inPoss: ['Arrive late in the box on crosses'],
  },
  'ball-winner': {
    outOfPoss: ['Aggressively hunt the ball whenever it enters your zone'],
  },
  'advanced-playmaker': {
    inPoss: ['Find pockets between the lines and turn forward'],
  },
  trequartista: {
    inPoss: ['Roam free between the lines — pull a CB out'],
  },
  'shadow-striker': {
    inPoss: ['Make late runs past the ST into the box'],
  },
  'inside-forward': {
    inPoss: ['Come inside onto your strong foot when the FB overlaps'],
  },
  'pure-winger': {
    inPoss: ['Stay wide — beat your man on the touchline'],
  },
  'wide-playmaker': {
    inPoss: ['Come inside to combine; play the through-ball to the FB run'],
  },
};

// ---------------------------------------------------------------------------
// Slider thresholds — additive bullets when a slider is dialled high/low
// ---------------------------------------------------------------------------

const SLIDER_HIGH = 70;
const SLIDER_LOW = 30;

function applySliderModifiers(
  resp: SlotResp,
  slot: string,
  sliders: Record<string, number>,
): void {
  const press = sliders.pressingIntensity ?? 50;
  const line = sliders.lineHeight ?? 50;
  const tempo = sliders.tempo ?? 50;
  const aggression = sliders.aggression ?? 50;
  const width = sliders.width ?? 50;

  if (press >= SLIDER_HIGH && ['ST', 'LW', 'RW', 'CAM'].includes(slot)) {
    resp.outOfPoss.push('Trigger the press on backwards passes');
  }
  if (line >= SLIDER_HIGH && ['CB', 'LB', 'RB', 'CDM'].includes(slot)) {
    resp.outOfPoss.push('Step up with the line on long balls');
  }
  if (line <= SLIDER_LOW && ['CB', 'LB', 'RB'].includes(slot)) {
    resp.outOfPoss.push('Sit deep — protect the space behind first');
  }
  if (tempo >= SLIDER_HIGH && ['CM', 'CDM', 'CAM'].includes(slot)) {
    resp.inPoss.push('First touch forward — quick combinations, no dwelling');
  }
  if (aggression >= SLIDER_HIGH && ['CDM', 'CM', 'CB'].includes(slot)) {
    resp.outOfPoss.push('Engage early — don’t let them turn');
  }
  if (width >= SLIDER_HIGH && ['LB', 'RB', 'LM', 'RM', 'LW', 'RW'].includes(slot)) {
    resp.inPoss.push('Hold the width — pin their wide players back');
  }
}

// ---------------------------------------------------------------------------
// Per-player instruction modifiers (PlayerRoleSelection.instructions)
// ---------------------------------------------------------------------------

function applyInstructionModifiers(
  resp: SlotResp,
  selection: PlayerRoleSelection,
  layout: TacticsLayout,
): void {
  const inst = selection.instructions || {};
  if (inst.attacking_intent === 'high') {
    resp.inPoss.push('Join the box late on switches');
  } else if (inst.attacking_intent === 'off') {
    resp.inPoss.push('Stay disciplined — hold your shape in attack');
  }
  if (inst.defensive_intent === 'high') {
    resp.outOfPoss.push('Aggressive ball recovery — engage early');
  }
  if (inst.press_trigger === 'aggressive') {
    resp.outOfPoss.push('Press on the first pass into your zone');
  } else if (inst.press_trigger === 'none') {
    resp.outOfPoss.push('Hold position — let the team set the trap');
  }
  if (inst.width === 'wide') {
    resp.inPoss.push('Stretch the pitch — stay touchline-wide');
  } else if (inst.width === 'narrow') {
    resp.inPoss.push('Tuck inside — leave the width to the FB');
  }
  if (inst.positioning === 'free-roam') {
    resp.inPoss.push('Free-roam — drift where the ball pulls you');
  } else if (inst.positioning === 'drift') {
    resp.inPoss.push('Drift into the half-space to find pockets');
  }
  if (selection.markTarget) {
    resp.outOfPoss.push(describeMarkTarget(selection.markTarget, layout));
  }
}

function describeMarkTarget(markTarget: string, layout: TacticsLayout): string {
  const m = markTarget.match(/^(home|away)-(\d+)$/);
  if (!m) return 'Track your man — see the board';
  const side = m[1] as 'home' | 'away';
  const idx = Number(m[2]);
  const spots = side === 'home' ? layout.homeSpots : layout.awaySpots;
  const opp = spots?.[idx];
  if (!opp) return 'Track your man — see the board';
  return `Mark their ${opp.role} on his runs from deep`;
}

// ---------------------------------------------------------------------------
// Arrow responsibilities — arrows bound to this player's playerKey
// ---------------------------------------------------------------------------

function applyArrowResponsibilities(
  resp: SlotResp,
  arrows: TacticsArrow[],
  playerKey: string,
  side: 'home' | 'away',
): void {
  for (const arrow of arrows) {
    if (arrow.playerKey !== playerKey) continue;
    const start = arrow.points[0];
    const end = arrow.points[arrow.points.length - 1];
    if (!start || !end) continue;
    const dx = end.x - start.x;
    const dz = end.z - start.z;
    // Home attacks toward -z; away attacks toward +z.
    const attackingDir = side === 'home' ? -1 : 1;
    const forwardMotion = Math.sign(dz) === attackingDir;
    const sideways = Math.abs(dx) > Math.abs(dz) * 1.2;
    let bullet: string;
    if (sideways) {
      bullet = dx > 0
        ? 'Move to the right-hand side as shown on the board'
        : 'Move to the left-hand side as shown on the board';
    } else if (forwardMotion) {
      bullet = 'Make the forward run shown on the board';
    } else {
      bullet = 'Drop back into the position shown on the board';
    }
    resp.inPoss.push(bullet);
  }
}

// ---------------------------------------------------------------------------
// Set-piece responsibilities — walk layout.setPieces, find this player's role
// ---------------------------------------------------------------------------

function applySetPieceResponsibilities(
  setPieces: unknown[],
  side: 'home' | 'away',
  x: number,
  z: number,
): string[] {
  const bullets: string[] = [];
  for (const raw of setPieces) {
    const sp = raw as Partial<TacticsSetPiece>;
    if (!sp || typeof sp !== 'object') continue;
    const spots = side === 'home' ? sp.homeSpots : sp.awaySpots;
    if (!spots || spots.length === 0) continue;
    // Find the closest spot in the set-piece layout to this player's resting position.
    let bestRole: string | null = null;
    let bestDist = Infinity;
    for (const s of spots) {
      if (!s || typeof s.x !== 'number' || typeof s.z !== 'number') continue;
      const d = Math.hypot(s.x - x, s.z - z);
      if (d < bestDist) {
        bestDist = d;
        bestRole = s.role ?? null;
      }
    }
    // Defensive: if the closest spot is more than ~30 m away, this player
    // probably has no specific role in this routine (defenders parked at
    // halfway during an attacking corner). Skip.
    if (bestRole == null || bestDist > 30) continue;
    const piece = sp.name || sp.type || 'set piece';
    bullets.push(describeSetPieceRole(piece, bestRole));
  }
  return bullets;
}

function describeSetPieceRole(piece: string, role: string): string {
  switch (role) {
    case 'CK': return `Take the corner on the "${piece}"`;
    case 'TI': return `Take the throw-in on the "${piece}"`;
    case 'NP': return `Near-post runner on the "${piece}"`;
    case 'FP': return `Far-post runner on the "${piece}"`;
    case 'SR': return `Short-corner option on the "${piece}"`;
    case 'SH': return `Short-throw option on the "${piece}"`;
    case 'TM': return `Third-man support on the "${piece}"`;
    case 'EB': return `Edge of box for rebounds on the "${piece}"`;
    case 'MK': return `Mark the short-option on the "${piece}"`;
    case 'PK': return `Take the penalty on the "${piece}"`;
    case 'FK': return `Take the free kick on the "${piece}"`;
    case 'W1':
    case 'W2':
    case 'W3':
    case 'W4': return `Stand in the wall on the "${piece}"`;
    default: return `Hold position on the "${piece}"`;
  }
}

// ---------------------------------------------------------------------------
// Engine
// ---------------------------------------------------------------------------

const MAX_BULLETS_PER_SECTION = 3;

function dedupe(arr: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of arr) {
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
  }
  return out;
}

function cap(arr: string[]): string[] {
  return arr.slice(0, MAX_BULLETS_PER_SECTION);
}

function readTeamTacticsConfig(
  layout: TacticsLayout,
  side: 'home' | 'away',
): TeamTacticsConfig {
  const bundle = layout.teamTactics;
  if (!bundle || !isTeamTacticsBundle(bundle)) return defaultTacticsConfig();
  const sideBundle = bundle[side];
  const active = sideBundle.slots.find((s) => s.id === sideBundle.activeSlotId);
  return active?.tactics ?? defaultTacticsConfig();
}

function readPlayerRole(
  layout: TacticsLayout,
  playerKey: string,
): PlayerRoleSelection | null {
  const roles = layout.playerRoles;
  if (!roles || typeof roles !== 'object') return null;
  const cand = (roles as Record<string, unknown>)[playerKey];
  if (!cand || !isPlayerRoleSelection(cand)) return null;
  return cand;
}

export function generatePlayerBrief(input: PlayerBriefInput): PlayerBrief {
  const { layout, side, playerKey } = input;
  const spots = side === 'home' ? layout.homeSpots : layout.awaySpots;
  const idx = Number(playerKey.split('-')[1]);
  const spot = Number.isFinite(idx) ? spots?.[idx] : undefined;
  const slot = spot?.role ?? 'CM';

  const baseline = SLOT_RESPONSIBILITIES[slot] ?? SLOT_RESPONSIBILITIES.CM!;
  const working: SlotResp = {
    inPoss: [...baseline.inPoss],
    outOfPoss: [...baseline.outOfPoss],
    transitions: [...baseline.transitions],
  };

  const cfg = readTeamTacticsConfig(layout, side);

  const buildMod = BUILD_UP_MODIFIERS[slot]?.[cfg.buildUpStyle];
  if (buildMod?.inPoss) working.inPoss.push(...buildMod.inPoss);
  if (buildMod?.outOfPoss) working.outOfPoss.push(...buildMod.outOfPoss);
  if (buildMod?.transitions) working.transitions.push(...buildMod.transitions);

  const defMod = DEFENSIVE_MODIFIERS[slot]?.[cfg.defensiveApproach];
  if (defMod?.inPoss) working.inPoss.push(...defMod.inPoss);
  if (defMod?.outOfPoss) working.outOfPoss.push(...defMod.outOfPoss);
  if (defMod?.transitions) working.transitions.push(...defMod.transitions);

  const selection = readPlayerRole(layout, playerKey);
  const namedRole = selection?.role ?? null;

  // Named-role overrides REPLACE the section baseline so a false-9 stops
  // reading like a target-man. Build-up + defensive mods stack on top
  // of the override; sliders + per-player instructions stack last so
  // the coach's explicit knob choices always win the final bullet.
  if (namedRole && ROLE_OVERRIDES[namedRole]) {
    const ov = ROLE_OVERRIDES[namedRole];
    if (ov.inPoss) working.inPoss = [...ov.inPoss];
    if (ov.outOfPoss) working.outOfPoss = [...ov.outOfPoss];
    if (ov.transitions) working.transitions = [...ov.transitions];
  }

  applySliderModifiers(working, slot, cfg.sliders);
  if (selection) applyInstructionModifiers(working, selection, layout);

  if (layout.arrows && layout.arrows.length > 0) {
    applyArrowResponsibilities(working, layout.arrows, playerKey, side);
  }

  const setPieceBullets = layout.setPieces && spot
    ? applySetPieceResponsibilities(
        layout.setPieces as unknown[],
        side,
        spot.x,
        spot.z,
      )
    : [];

  return {
    playerKey,
    slot,
    namedRole,
    inPossession: cap(dedupe(working.inPoss)),
    outOfPossession: cap(dedupe(working.outOfPoss)),
    transitions: cap(dedupe(working.transitions)),
    setPieces: cap(dedupe(setPieceBullets)),
    source: 'rules',
    generatedAt: new Date().toISOString(),
  };
}

export function generateAllPlayerBriefs(
  layout: TacticsLayout,
  side: 'home' | 'away',
): PlayerBrief[] {
  const spots = side === 'home' ? layout.homeSpots : layout.awaySpots;
  if (!spots) return [];
  return spots.map((_, idx) =>
    generatePlayerBrief({ layout, side, playerKey: `${side}-${idx}` }),
  );
}

// ---------------------------------------------------------------------------
// Edit history — capped per-player undo stack
// ---------------------------------------------------------------------------

const MAX_HISTORY_PER_PLAYER = 5;

/**
 * Push a brief onto the playerKey's history stack. Newest first; older
 * entries fall off when the stack exceeds 5. Pure — returns a new
 * history map. Identical-content briefs are NOT duplicated (avoids the
 * stack filling up with no-op saves during rapid edits).
 */
export function appendBriefHistory(
  history: Record<string, PlayerBrief[]> | undefined,
  playerKey: string,
  brief: PlayerBrief,
): Record<string, PlayerBrief[]> {
  const next = { ...(history ?? {}) };
  const prior = next[playerKey] ?? [];
  if (prior[0] && briefContentEqual(prior[0], brief)) return next;
  next[playerKey] = [brief, ...prior].slice(0, MAX_HISTORY_PER_PLAYER);
  return next;
}

function briefContentEqual(a: PlayerBrief, b: PlayerBrief): boolean {
  return (
    a.slot === b.slot &&
    a.namedRole === b.namedRole &&
    a.source === b.source &&
    arrEqual(a.inPossession, b.inPossession) &&
    arrEqual(a.outOfPossession, b.outOfPossession) &&
    arrEqual(a.transitions, b.transitions) &&
    arrEqual(a.setPieces, b.setPieces)
  );
}

function arrEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
